import { clsx, type ClassValue } from "clsx";
import { isValidElement, type ReactNode } from "react";
import { extendTailwindMerge } from "tailwind-merge";

// Animations defined outside Tailwind's default theme (tailwindcss-animate's
// in/out, ours in styles.css), so a caller's `animate-*` replaces a primitive's.
const twMerge = extendTailwindMerge({
  extend: { theme: { animate: ["in", "out", "collapsible-down", "collapsible-up"] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return extractText(props?.children);
  }
  return "";
}

/**
 * Safely extract the hostname from a URL string.
 * Returns the raw string if parsing fails.
 */
export function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/**
 * Slug → sentence-case prose ("cash_tracker" → "Cash tracker"). Only
 * underscores become spaces; every other character survives, so the result
 * re-slugs back to the input for any grammar that maps `_` ↔ space — staged
 * folder names (`STAGED_FOLDER_NAME_PATTERN`) and template variable keys both
 * round-trip through it on every keystroke.
 *
 * Deliberately not `humanizeType(t, { titleCase: false })` or `humanizeToken`:
 * those also collapse `-` and `.` into spaces, which silently renames a
 * hyphenated name ("their-imports" → "their_imports") and orphans its refs.
 */
export function humanizeSlug(slug: string): string {
  const words = slug.replace(/_+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

/** First non-empty, trimmed candidate; undefined if none. */
export function resolveDisplayName(
  ...candidates: (string | null | undefined)[]
): string | undefined {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

/** Best user-facing label for a user/actor: name → email → id. Returns undefined if none. */
export function resolveUserLabel(input: {
  name?: string | null | undefined;
  email?: string | null | undefined;
  id?: string | null | undefined;
}): string | undefined {
  return resolveDisplayName(input.name, input.email, input.id);
}

/** Best user-facing label for a resource: name → id. Returns undefined if neither. */
export function resolveResourceLabel(input: {
  name?: string | null | undefined;
  id?: string | null | undefined;
}): string | undefined {
  return resolveDisplayName(input.name, input.id);
}

/**
 * The kind of actor an identity refers to. Lives here (rather than in
 * `identity-display.tsx`) so link-resolution helpers can depend on it without a
 * circular import; `IdentityDisplay` re-exports it for established call sites.
 */
export type IdentityKind =
  | "user"
  | "agent"
  | "system"
  | "api_key"
  | "oauth_client"
  | "custom_app"
  | "unknown";

/**
 * Canonical workspace-relative profile path for a linkable identity, or
 * `undefined` when the identity isn't linkable (no id/slug, or a non-linkable
 * kind like `system`).
 *
 * This is the single source of truth for "where does clicking an actor go" —
 * every actor surface (assignee cells, timeline actors, comment authors, agent
 * headers, …) resolves links through here instead of hand-building
 * `/w/:slug/...` strings, so adding a new linkable kind only changes one place.
 */
export function resolveIdentityHref(input: {
  kind?: IdentityKind | null | undefined;
  publicId?: string | null | undefined;
  workspaceSlug?: string | null | undefined;
}): string | undefined {
  const { kind, publicId, workspaceSlug } = input;
  if (!publicId || !workspaceSlug) return undefined;
  switch (kind) {
    case "user":
      return `/w/${workspaceSlug}/users/${publicId}`;
    case "agent":
      return `/w/${workspaceSlug}/agents/${publicId}`;
    default:
      // `system`, credential (`api_key` / `oauth_client`), app, and `unknown`
      // actors have no profile page to link to.
      return undefined;
  }
}

/**
 * Resolve a drop's `DataTransfer` into plain `File`s, expanding dropped
 * folders into the files they contain.
 *
 * `dataTransfer.files` represents a dropped directory as a zero-byte `File`
 * with no type and no extension — uploading it fails as an unsupported type
 * while the folder's real contents never upload (QUO-274). Chromium/WebKit
 * expose the directory tree via `DataTransferItem.webkitGetAsEntry()`, so we
 * walk it instead.
 *
 * The entries must be captured synchronously inside the drop event handler
 * (the item list is neutered once the handler returns), so callers invoke
 * this synchronously from `onDrop` and await the returned promise.
 */
export function filesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const fallback = Array.from(dataTransfer.files ?? []);
  const items = Array.from(dataTransfer.items ?? []);

  // `webkitGetAsEntry` is missing in some engines/tests; if we can't get an
  // entry for every item, trust `dataTransfer.files` as-is rather than
  // guessing which items were folders.
  const entries = items
    .filter((item) => item.kind === "file")
    .map((item) => item.webkitGetAsEntry?.() ?? null);
  if (entries.length === 0 || entries.some((entry) => entry === null)) {
    return Promise.resolve(fallback);
  }

  return collectFiles(entries as FileSystemEntry[]).catch(() => fallback);
}

async function collectFiles(entries: FileSystemEntry[]): Promise<File[]> {
  const out: File[] = [];
  for (const entry of entries) {
    if (entry.isFile) {
      // Tolerate a single unreadable child (moved/deleted between drop and
      // read): skipping it keeps the rest of the folder uploading, whereas
      // propagating would fall back to `dataTransfer.files` — the directory
      // pseudo-File, i.e. the exact QUO-274 failure this module exists to fix.
      try {
        out.push(await entryFile(entry as FileSystemFileEntry));
      } catch {
        // skip unreadable file
      }
    } else if (entry.isDirectory) {
      let children: FileSystemEntry[];
      try {
        children = await readAllEntries(entry as FileSystemDirectoryEntry);
      } catch {
        continue;
      }
      // Skip hidden files while walking a folder (Finder drops always carry
      // `.DS_Store`, which would fail every folder upload with an
      // unsupported-type error). Files the user dropped directly are never
      // filtered — they only reach the `isFile` branch above.
      out.push(...(await collectFiles(children.filter((c) => !c.name.startsWith(".")))));
    }
  }
  return out;
}

function entryFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/**
 * Choose a Markdown code fence long enough to survive backtick runs inside
 * `value`. Untrusted content (LLM transcripts, eval payloads, a file that is
 * itself a markdown doc) can contain ``` sequences that would otherwise close
 * a fixed-length fence early and let the remainder render as markdown —
 * images and links included. Always use one more backtick than the longest
 * run the value contains, never fewer than three.
 */
export function fenceFor(value: string): string {
  const longestRun = (value.match(/`+/g) ?? []).reduce((max, run) => Math.max(max, run.length), 0);
  return "`".repeat(Math.max(3, longestRun + 1));
}

async function readAllEntries(dir: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  // `readEntries` returns at most ~100 entries per call (Chromium); keep
  // reading until it hands back an empty batch.
  const reader = dir.createReader();
  const all: FileSystemEntry[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (batch.length === 0) return all;
    all.push(...batch);
  }
}

/** Stable index into a palette of `size` colours, so a key always maps to the same colour. */
export function paletteIndex(key: string, size: number): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % size;
}
